import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getJournals, getVerboseIssues, sortArticlesBySequence } from '../services/firebaseService';
import assetService from '../services/assetService';
import { getArticlePageUrl } from '../utils/journalUrlHelper';
import { getJournalTypeDisplayName } from '../config/journalTypes';
import { getBestFileUrl } from '../utils/fileUrlConverter';
import {
  FaFilePdf,
  FaArrowRight,
  FaArchive,
} from 'react-icons/fa';
import { usePageContent } from '../services/pageContentService';
import RawHtmlPage, { isRawHtmlPage } from '../components/RawHtmlPage';
import IssueArticleGroups from '../components/IssueArticleGroups';
import { formatIssueRecord } from '../utils/issueFormat';

const getNum = (val) => parseInt(String(val || '').replace(/\D/g, ''), 10) || 0;

// Year an issue belongs to, from its validity date or creation date
const getIssueYear = (iss) => {
  for (const val of [iss.validity, iss.createdAt]) {
    const match = String(val || '').match(/\b(19|20)\d{2}\b/);
    if (match) return parseInt(match[0], 10);
  }
  return null;
};

const IssuesPage = () => {
  const { content: pageContent } = usePageContent('archives');
  const location = useLocation();
  const isArchivesRoute = location.pathname.startsWith('/archives');

  const [journals, setJournals] = useState([]);
  const [issues, setIssues] = useState([]);
  const [search] = useState(''); // search box is currently hidden
  const [selectedArchivedIssue, setSelectedArchivedIssue] = useState(null);

  useEffect(() => {
    const cachedJournals = assetService.getCache('journals');
    const cachedIssues = assetService.getCache('issues');

    // Show cached data immediately, then always refresh so edits aren't hidden by a stale cache
    if (cachedJournals && cachedIssues) {
      setJournals(sortArticlesBySequence(cachedJournals));
      setIssues(cachedIssues);
    }
    Promise.all([getJournals(), getVerboseIssues()]).then(([jList, iList]) => {
      const sortedJList = sortArticlesBySequence(jList || []);
      setJournals(sortedJList);
      setIssues(iList || []);
      assetService.setCache('journals', sortedJList);
      assetService.setCache('issues', iList);
    });
  }, []);

  // 1. Current active issue (strictly isCurrentIssue)
  const currentIssue = useMemo(() => {
    return (
      issues.find((i) => i.isCurrentIssue) ||
      issues[0] || {
        Vol: '1',
        issue: '1',
        validity: 'January to June 2026',
        isCurrentIssue: true,
      }
    );
  }, [issues]);

  // 2. Archive lists every published issue, grouped by volume (newest first)
  const archivedVolumes = useMemo(() => {
    const map = {};
    issues
      .filter((i) => !i.isUpcoming)
      .forEach((iss) => {
        const vol = getNum(iss.Vol) || 1;
        if (!map[vol]) map[vol] = { vol, issues: [], years: [] };
        map[vol].issues.push(iss);
        const year = getIssueYear(iss);
        if (year) map[vol].years.push(year);
      });
    return Object.values(map)
      .sort((a, b) => b.vol - a.vol)
      .map((v) => ({
        ...v,
        year: v.years.length ? Math.min(...v.years) : null,
        issues: [...v.issues].sort((a, b) => getNum(b.issue) - getNum(a.issue)),
      }));
  }, [issues]);

  // Current issue journals list sorted by sequence
  const currentIssueJournals = useMemo(() => {
    const filtered = journals.filter((j) => j.isCurrentIssue);
    const list = filtered.length > 0 ? filtered : journals.slice(0, 10);
    return sortArticlesBySequence(list);
  }, [journals]);

  // Determine active articles to display, strictly sorted by sequence
  const activeArticles = useMemo(() => {
    if (isArchivesRoute && selectedArchivedIssue) {
      const list = journals.filter((j) => {
        const jVol = String(j.volume || j.Vol || '').replace(/[^0-9]/g, '');
        const aVol = String(selectedArchivedIssue.Vol || '').replace(/[^0-9]/g, '');
        const jIss = String(j.issue || '').replace(/[^0-9]/g, '');
        const aIss = String(selectedArchivedIssue.issue || '').replace(/[^0-9]/g, '');
        return (!aVol || jVol === aVol) && (!aIss || jIss === aIss);
      });
      return sortArticlesBySequence(list);
    }
    return sortArticlesBySequence(currentIssueJournals);
  }, [isArchivesRoute, selectedArchivedIssue, currentIssueJournals, journals]);

  const filteredArticles = useMemo(() => {
    let result = activeArticles;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = activeArticles.filter(
        (a) =>
          (a.title && a.title.toLowerCase().includes(q)) ||
          (a.authors && a.authors.toLowerCase().includes(q)) ||
          (a.type && a.type.toLowerCase().includes(q))
      );
    }
    return sortArticlesBySequence(result);
  }, [activeArticles, search]);

  if (isArchivesRoute && isRawHtmlPage(pageContent)) {
    return <RawHtmlPage html={pageContent.full_content} />;
  }

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">

      {/* ================= BREADCRUMB BAR ================= */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          {isArchivesRoute ? (
            <>
              {selectedArchivedIssue ? (
                <>
                  <button
                    onClick={() => setSelectedArchivedIssue(null)}
                    className="text-[#03478E] hover:text-[#023366] hover:underline font-medium"
                  >
                    Archives
                  </button>
                  <span>&gt;</span>
                  <span className="font-semibold text-gray-700">
                    {formatIssueRecord(selectedArchivedIssue)}
                  </span>
                </>
              ) : (
                <span className="font-semibold text-gray-700">Archives</span>
              )}
            </>
          ) : (
            <span className="font-semibold text-gray-700">Current Issue</span>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">

        {/* ================= CASE 1: ARCHIVES ROUTE ================= */}
        {isArchivesRoute ? (
          <>
            {/* Header Block for Archives */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-2">
              <div>
                {/* <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
                  JOURNAL ARCHIVES
                </span> */}
                <h1 className="page-title">
                  {selectedArchivedIssue
                    ? formatIssueRecord(selectedArchivedIssue)
                    : 'Archives'}
                </h1>
                {/* <p className="text-base text-gray-600 font-medium mt-1">
                  {selectedArchivedIssue
                    ? formatIssueDate(selectedArchivedIssue.validity)
                    : 'Access previously published volumes and archived scholarly articles.'}
                </p> */}
              </div>

              {selectedArchivedIssue && (
                <button
                  onClick={() => setSelectedArchivedIssue(null)}
                  className="text-xs font-bold text-[#03478E] hover:text-[#023366] underline self-start md:self-end"
                >
                  ← Back to All Archives
                </button>
              )}
            </div>

            {/* If an archived issue is selected, show its table of contents */}
            {selectedArchivedIssue ? (
              <section className="bg-white rounded-3xl overflow-hidden shadow-sm">
                <div className="bg-[#03478E] px-6 sm:px-8 py-3 flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-white">
                    Archived Table of Contents
                  </h2>
                  {/* <span className="text-xs text-blue-100 font-semibold">
                    {filteredArticles.length} Article{filteredArticles.length !== 1 ? 's' : ''}
                  </span> */}
                </div>
                <div className="divide-y divide-gray-100 p-4 sm:p-6">
                  {filteredArticles.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 text-sm">
                      No articles cataloged for this archived issue.
                    </div>
                  ) : (
                    filteredArticles.map((article, idx) => {
                      const articleUrl = getArticlePageUrl(article, idx);
                      const typeLabel = article.type ? getJournalTypeDisplayName(article.type) : 'Scholarly Article';
                      const pdfUrl = getBestFileUrl(article);

                      return (
                        <div
                          key={article.id || idx}
                          className="p-5 hover:bg-blue-50/50 rounded-2xl transition-all duration-200 flex flex-col md:flex-row md:items-center justify-between gap-4"
                        >
                          <div className="space-y-2 flex-1 pr-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* <span className="text-xs font-mono font-bold text-[#03478E] bg-blue-50 border border-blue-200/60 px-2 py-0.5 rounded-md">
                                #{article.articleNumber || idx + 1}
                              </span> */}
                              <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full text-[#03478E] bg-blue-50/80">
                                {typeLabel}
                              </span>
                            </div>
                            <h3 className="text-base font-bold text-default leading-snug hover:text-[#03478E]">
                              <Link to={articleUrl} className="hover:text-[#03478E]">{article.title}</Link>
                            </h3>
                            <div className="text-sm text-gray-500 italic">
                              {article.authors ? (article.authors.startsWith('Dr.') ? article.authors : `Dr. ${article.authors}`) : 'AIMJHM Authors'}
                            </div>
                          </div>

                          <div className="flex items-center gap-4 flex-shrink-0 self-start md:self-center">
                            <Link
                              to={articleUrl}
                              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#03478E] hover:text-white bg-blue-50 hover:bg-[#03478E] px-4 py-2 rounded-full transition-all duration-200 shadow-xs"
                            >
                              <span>View Article</span>
                              <FaArrowRight className="text-[10px]" />
                            </Link>

                            {pdfUrl && (
                              <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-white bg-red-50 hover:bg-red-600 px-3.5 py-2 rounded-full transition-all duration-200 shadow-xs"
                              >
                                <FaFilePdf />
                                <span>Get PDF</span>
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            ) : (
              /* If no archived issue is selected, list every volume and issue */
              <section className="bg-white rounded-3xl shadow-sm px-6 sm:px-10 py-8">
                {archivedVolumes.length > 0 ? (
                  archivedVolumes.map((vol, vIdx) => (
                    <div key={vol.vol}>
                      {vIdx > 0 && <hr className="border-t border-gray-200 my-8 sm:ml-24" />}
                      <h2 className="section-heading mb-4">
                        {vol.year ? ` ${vol.year}` : ''}
                      </h2>
                      <ul className="space-y-3">
                        {vol.issues.map((iss) => (
                          <li key={iss.id || iss.issue} className="font-bold text-base text-default">
                            <button
                              onClick={() => setSelectedArchivedIssue(iss)}
                              className="text-[#03478E] hover:text-[#023366] hover:underline text-left"
                            >
                              {formatIssueRecord({ ...iss, Vol: vol.vol })}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                ) : (
                  <div className="text-center space-y-4 py-6">
                    <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 flex items-center justify-center text-[#03478E] text-2xl">
                      <FaArchive />
                    </div>
                    <h3 className="sub-heading">
                      No Issues Published Yet
                    </h3>
                  </div>
                )}
              </section>
            )}
          </>
        ) : (
          /* ================= CASE 2: CURRENT ISSUE ROUTE ================= */
          <>
            {/* Header Block for Current Issue */}
            {/* <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-2">
              <div>
                <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
                  CURRENT ISSUE
                </span>
                <h1 className="page-title">
                  Volume {currentIssue.Vol || '1'}, {formatIssueLabel(currentIssue.issue)}
                </h1>
                <p className="text-base text-gray-600 font-medium mt-1">
                  {formatIssueDate(currentIssue.validity)}
                </p>
              </div>

              <div className="w-full md:w-72">
                <div className="relative">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search articles in issue..."
                    className="w-full text-xs pl-9 pr-4 py-3 rounded-full bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                  <FaSearch className="absolute left-3.5 top-3.5 text-gray-400 text-xs" />
                </div>
              </div>
            </div> */}

            {/* Table of Contents for Current Issue */}
            <section className="bg-white rounded-3xl overflow-hidden shadow-sm">
              <div className="bg-[#03478E] px-6 sm:px-8 py-3 flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-sm font-bold uppercase tracking-wider text-white">
                  {formatIssueRecord(currentIssue)}
                </h2>
              </div>

              <IssueArticleGroups
                articles={filteredArticles}
                emptyMessage={search.trim() ? 'No articles found matching your query.' : 'No articles published in this issue yet.'}
              />
            </section>

          </>
        )}

      </div>
    </div>
  );
};

export default IssuesPage;
